import { lazy, type ComponentType } from "react";

/** Resolve quando o elemento com `id` chega perto da tela. */
export function whenNearViewport(id: string, margin = "600px"): Promise<void> {
  return new Promise((resolve) => {
    const start = () => {
      const el = document.getElementById(id);
      if (!el || typeof IntersectionObserver === "undefined") return resolve();
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            resolve();
          }
        },
        { rootMargin: `${margin} 0px ${margin} 0px` },
      );
      io.observe(el);
    };
    const schedule = () => {
      const requestIdle = window.requestIdleCallback ?? ((callback: IdleRequestCallback) => {
        return window.setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 0 }), 300);
      });
      requestIdle(start, { timeout: 800 });
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", schedule, { once: true });
    } else schedule();
  });
}

/**
 * Componente cujo JavaScript só baixa/hidrata quando a seção `id` chega perto
 * da tela. Use no cliente com um ternário para o servidor renderizar o HTML
 * completo:  import.meta.env.SSR ? Static : lazyWhenVisible("id", () => import(...))
 * No cliente o React mantém o HTML do servidor intacto (Suspense desidratado)
 * até o módulo chegar. Use sempre dentro de <Suspense>.
 */
export function lazyWhenVisible<P extends object>(
  id: string,
  factory: () => Promise<{ default: ComponentType<P> }>,
) {
  return lazy(() => whenNearViewport(id).then(factory));
}
